import { __rest } from "tslib";
import { FieldPolicy, Reference } from "@apollo/client";
import { mergeDeep } from "@apollo/client/utilities/common/mergeDeep.js";

type KeyArgs = FieldPolicy<any>["keyArgs"];

// A very basic pagination field policy that always concatenates new
// results onto the existing array, without examining options.args.
export function concatPagination<T = Reference>(
  keyArgs: KeyArgs = false
): FieldPolicy<T[]> {
  return {
    keyArgs,
    merge(existing, incoming) {
      return existing ? [...existing, ...incoming] : incoming;
    },
  };
}

// A basic field policy that uses options.args.{offset,limit} to splice
// the incoming data into the existing array. If your arguments are called
// something different (like args.{start,count}), feel free to copy/paste
// this implementation and make the appropriate changes.
export function offsetLimitPagination<T = Reference>(
  keyArgs: KeyArgs = false
): FieldPolicy<T[]> {
  return {
    keyArgs,
    merge(existing, incoming, { args }) {
      const merged = existing ? existing.slice(0) : [];
      if (args) {
        // Assume an offset of 0 if args.offset omitted.
        const { offset = 0 } = args;
        for (let i = 0; i < incoming.length; ++i) {
          merged[offset + i] = incoming[i];
        }
      } else {
        // It's unusual (probably a mistake) for a paginated field not
        // to receive any arguments, so you might prefer to throw an
        // exception here, instead of recovering by appending incoming
        // onto the existing array.
        merged.push(...(incoming as any));
      }
      return merged;
    },
  };
}

// Whether TRelayEdge<TNode> is a normalized Reference or a non-normalized
// object, it needs a .cursor property where the relayStylePagination
// merge function can store cursor strings taken from pageInfo. Storing an
// extra reference.cursor property should be safe, and is easier than
// attempting to update the cursor field of the normalized StoreObject
// that the reference refers to, or managing edge wrapper objects
// (something I attempted in #7023, but abandoned because of #7088).
export type TRelayEdge<TNode> =
  | {
      cursor?: string | null;
      node: TNode;
    }
  | (Reference & { cursor?: string | null });

export type TRelayPageInfo = {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  startCursor: string;
  endCursor: string;
};

export type TExistingRelay<TNode> = Readonly<{
  edges: TRelayEdge<TNode>[];
  pageInfo: TRelayPageInfo;
}>;

export type TIncomingRelay<TNode> = {
  edges?: TRelayEdge<TNode>[];
  pageInfo?: TRelayPageInfo;
};

export type RelayFieldPolicy<TNode> = FieldPolicy<
  TExistingRelay<TNode>,
  TIncomingRelay<TNode>,
  TIncomingRelay<TNode>
>;

// How a cursor-less incoming page (neither `args.after` nor `args.before`)
// is merged with accumulated edges. "replace" is the historical behavior.
// "union-overlap" is opt-in per field — see RelayStylePaginationOptions.
export type CursorlessMergeMode = "replace" | "union-overlap";

export type RelayStylePaginationOptions = {
  cursorlessMerge?: CursorlessMergeMode;
};

// As proof of the flexibility of field policies, this function generates
// one that handles Relay-style pagination, without Apollo Client knowing
// anything about connections, edges, cursors, or pageInfo objects.
export function relayStylePagination<TNode extends Reference>(
  keyArgs: KeyArgs = false,
  { cursorlessMerge = "replace" }: RelayStylePaginationOptions = {}
): RelayFieldPolicy<TNode> {
  return {
    keyArgs,

    read(existing, { canRead, readField }) {
      if (!existing) return;

      const edges: TRelayEdge<TNode>[] = [];
      let startCursor = "";
      let endCursor = "";
      existing.edges.forEach(edge => {
        // Edges themselves could be Reference objects, so it's important
        // to use readField to access the edge.edge.node property.
        if (canRead(readField("node", edge))) {
          edges.push(edge);
          if (edge.cursor) {
            startCursor = startCursor || edge.cursor;
            endCursor = edge.cursor;
          }
        }
      });

      return {
        // Some implementations return additional Connection fields, such
        // as existing.totalCount. These fields are saved by the merge
        // function, so the read function should also preserve them.
        ...getExtras(existing),
        edges,
        pageInfo: {
          ...existing.pageInfo,
          startCursor,
          endCursor,
        },
      };
    },

    merge(
      existing = makeEmptyData(),
      incoming,
      { args, isReference, readField }
    ) {
      const incomingEdges = incoming.edges
        ? incoming.edges.map(edge => {
            if (isReference((edge = { ...edge }))) {
              // In case edge is a Reference, we read out its cursor field and
              // store it as an extra property of the Reference object.
              edge.cursor = readField<string>("cursor", edge);
            }
            return edge;
          })
        : [];

      if (incoming.pageInfo) {
        const { pageInfo } = incoming;
        const { startCursor, endCursor } = pageInfo;
        const firstEdge = incomingEdges[0];
        const lastEdge = incomingEdges[incomingEdges.length - 1];
        // In case we did not request the cursor field for edges in this
        // query, we can still infer cursors from pageInfo.
        if (firstEdge && startCursor) {
          firstEdge.cursor = startCursor;
        }
        if (lastEdge && endCursor) {
          lastEdge.cursor = endCursor;
        }
        // Cursors can also come from edges, so we default
        // pageInfo.{start,end}Cursor to {first,last}Edge.cursor.
        const firstCursor = firstEdge && firstEdge.cursor;
        if (firstCursor && !startCursor) {
          incoming = mergeDeep(incoming, {
            pageInfo: {
              startCursor: firstCursor,
            },
          });
        }
        const lastCursor = lastEdge && lastEdge.cursor;
        if (lastCursor && !endCursor) {
          incoming = mergeDeep(incoming, {
            pageInfo: {
              endCursor: lastCursor,
            },
          });
        }
      }

      let prefix = existing.edges;
      let suffix: typeof prefix = [];

      if (!incoming.edges) {
        // A payload carrying no `edges` field at all — e.g. the
        // `MissingPermissions` member of a union-typed connection field.
        // There is nothing to splice, and the cursor the request happened
        // to carry says nothing about which accumulated edges to keep, so
        // retain all of them and let the payload's own fields
        // (`__typename`, `missingPermissions`, …) merge over the stored
        // object below, so the UI reads the permission state.
        // prefix = existing.edges; suffix = []; // already true
      } else if (args && args.after) {
        // This comparison does not need to use readField("cursor", edge),
        // because we stored the cursor field of any Reference edges as an
        // extra property of the Reference object.
        const index = prefix.findIndex(edge => edge.cursor === args.after);
        // Splicing is sound only while `after` still identifies the END of the
        // accumulated edges — which is what every caller passes (each
        // `fetchMore` here sends `pageInfo.endCursor`). Two ways it stops
        // holding, and both mean this response is stale:
        //
        //  - The cursor is GONE (index < 0) — e.g. a fetchMore still in flight
        //    lands after the connection was replaced wholesale by a cursor-less
        //    refetch. Splicing would keep the whole stale prefix and append the
        //    incoming page after it, silently dropping everything that used to
        //    sit beyond the (now missing) boundary, and the pageInfo guards
        //    below would then advance endCursor/hasNextPage past that gap,
        //    making the skipped pages permanently unreachable.
        //
        //  - The cursor is still PRESENT but is no longer the tail
        //    (index < prefix.length - 1) — the connection grew while this
        //    request was in flight, either from a concurrent fetchMore or from
        //    a `union-overlap` refetch that retained a tail below the boundary.
        //    Slicing back to it would discard those edges — rows the user can
        //    already see, so they visibly vanish — and because this branch
        //    leaves `suffix` empty, the pageInfo guards below would REWIND
        //    endCursor/hasNextPage to this older page's, moving the pagination
        //    boundary backwards.
        //
        // Nothing is lost by dropping the response: every edge it carries is
        // still reachable from the current tail. An empty accumulated list is
        // covered by `index < 0` (its `prefix.length - 1` is also -1).
        if (index < 0 || index < prefix.length - 1) {
          return existing;
        }
        // `index` is the last edge, so the prefix is already the whole list and
        // needs no slicing.
        // suffix = []; // already true
      } else if (args && args.before) {
        const index = prefix.findIndex(edge => edge.cursor === args.before);
        if (index < 0) {
          // Symmetric bail-out for a stale `before` cursor — see above.
          return existing;
        }
        suffix = prefix.slice(index);
        prefix = [];
      } else {
        if (cursorlessMerge === "union-overlap") {
          // Cursor-less refetch of an append-only, keyset-ordered
          // connection (e.g. the change-log): try to union the fresh
          // incoming head with the accumulated tail instead of discarding
          // it. See the field policy comment at the call site for why
          // this is safe, and design D1/D4 for the three-rule order.
          const existingEdges = prefix;
          const lastExistingEdge = existingEdges[existingEdges.length - 1];
          // Both rules identify a row by cursor equality, so they compare
          // cursors only when the edge in hand actually carries one — two
          // edges that merely both lack a cursor are not the same row, and
          // matching them would union at a bogus offset. A cursor-less edge
          // therefore fails its rule and falls through to the replace below.
          const lastExistingCursor = lastExistingEdge?.cursor;
          // Rule 1: no last existing edge (cold mount) trivially satisfies
          // "the incoming page covers everything cached".
          const coveredByIncoming =
            !lastExistingEdge ||
            (Boolean(lastExistingCursor) &&
              incomingEdges.some(edge => edge.cursor === lastExistingCursor));
          if (!coveredByIncoming) {
            const lastIncomingCursor =
              incomingEdges[incomingEdges.length - 1]?.cursor;
            const overlapIndex = lastIncomingCursor
              ? existingEdges.findIndex(
                  edge => edge.cursor === lastIncomingCursor
                )
              : -1;
            if (overlapIndex >= 0) {
              // Rule 2: union. (A match on the *last* existing edge implies
              // that edge has a cursor, so rule 1 already caught it — this
              // suffix is therefore always non-empty.)
              suffix = existingEdges.slice(overlapIndex + 1);
            }
            // else rule 3: no shared cursor (a gap, or an empty incoming
            // page) — fall through to the replace below.
          }
        }
        // Either the default mode, or none of the union rules applied:
        // the incoming edges cannot be spliced into the existing edges,
        // so they must replace the existing edges (prefix). See #6592
        // for a motivating example of the historical replace behavior.
        prefix = [];
      }

      const edges = [...prefix, ...incomingEdges, ...suffix];

      const pageInfo: TRelayPageInfo = {
        // The ordering of these two ...spreads may be surprising, but it
        // makes sense because we want to combine PageInfo properties with a
        // preference for existing values, *unless* the existing values are
        // overridden by the logic below, which is permitted only when the
        // incoming page falls at the beginning or end of the data.
        ...incoming.pageInfo,
        ...existing.pageInfo,
      };

      if (incoming.pageInfo) {
        const {
          hasPreviousPage,
          hasNextPage,
          startCursor,
          endCursor,
          ...extras
        } = incoming.pageInfo;

        // If incoming.pageInfo had any extra non-standard properties,
        // assume they should take precedence over any existing properties
        // of the same name, regardless of where this page falls with
        // respect to the existing data.
        Object.assign(pageInfo, extras);

        // Keep existing.pageInfo.has{Previous,Next}Page unless the
        // placement of the incoming edges means incoming.hasPreviousPage
        // or incoming.hasNextPage should become the new values for those
        // properties in existing.pageInfo. Note that these updates are
        // only permitted when the beginning or end of the incoming page
        // coincides with the beginning or end of the existing data, as
        // determined using prefix.length and suffix.length.
        if (!prefix.length) {
          if (void 0 !== hasPreviousPage)
            pageInfo.hasPreviousPage = hasPreviousPage;
          if (void 0 !== startCursor) pageInfo.startCursor = startCursor;
        }
        if (!suffix.length) {
          if (void 0 !== hasNextPage) pageInfo.hasNextPage = hasNextPage;
          if (void 0 !== endCursor) pageInfo.endCursor = endCursor;
        }
      }

      return {
        ...getExtras(existing),
        ...getExtras(incoming),
        edges,
        pageInfo,
      };
    },
  };
}

// Returns any unrecognized properties of the given object.
const getExtras = (obj: Record<string, any>) => __rest(obj, notExtras);
const notExtras = ["edges", "pageInfo"];

function makeEmptyData(): TExistingRelay<any> {
  return {
    edges: [],
    pageInfo: {
      hasPreviousPage: false,
      hasNextPage: true,
      startCursor: "",
      endCursor: "",
    },
  };
}

export function addEntryToEdges<O extends { id: string }>(
  existing: TExistingRelay<O> & { totalCount: number },
  edge: TRelayEdge<O>
): TExistingRelay<O> & { totalCount: number } {
  const edges = [edge, ...existing.edges];
  return {
    ...existing,
    totalCount: existing.totalCount + 1,
    edges: edges,
    pageInfo: {
      ...existing.pageInfo,
      startCursor: edge.cursor || existing.pageInfo.startCursor,
      ...(existing.edges.length === 0
        ? { endCursor: edge.cursor || "" }
        : undefined),
    },
  };
}

export function removeEntryFromEdges<O extends { id: string }>(
  existing: TExistingRelay<O> & { totalCount: number },
  edge: TRelayEdge<O>
): TExistingRelay<O> & { totalCount: number } {
  const edges = existing.edges.filter(
    e => "node" in e && "node" in edge && e.node.id !== edge.node.id
  );
  return {
    ...existing,
    totalCount: Math.max(existing.totalCount - 1, 0),
    edges,
    pageInfo: {
      ...existing.pageInfo,
      startCursor:
        edges.length > 0
          ? existing.pageInfo.startCursor === edge.cursor
            ? edges[0].cursor || existing.pageInfo.startCursor
            : existing.pageInfo.startCursor
          : "",
      endCursor:
        edges.length > 0
          ? existing.pageInfo.endCursor === edge.cursor
            ? edges[edges.length - 1].cursor || existing.pageInfo.endCursor
            : existing.pageInfo.endCursor
          : "",
    },
  };
}
